import React, { useState, useEffect } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LockResetIcon from '@mui/icons-material/LockReset';
import CloseIcon from '@mui/icons-material/Close';
import BadgeIcon from '@mui/icons-material/Badge';
import EmailIcon from '@mui/icons-material/Email';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuthStore } from '../../stores/authStore';
import { getUserProfile, updateUser } from '../../features/user/services/usersAPI';
import styles from './SettingsModal.module.css';

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    themeMode: 'light' | 'dark';
    onToggleThemeMode: (mode: 'light' | 'dark') => void;
    themeShape?: 'square' | 'rounded';
    onToggleThemeShape?: (shape: 'square' | 'rounded') => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
    isOpen,
    onClose,
    themeMode,
    onToggleThemeMode,
    themeShape = 'square',
    onToggleThemeShape
}) => {
    const [activeTab, setActiveTab] = useState<'profile' | 'theme'>('profile');
    const authUser = useAuthStore((state) => state.user);
    const [profileData, setProfileData] = useState<any>(null);
    const [loadingProfile, setLoadingProfile] = useState(false);

    // Form de Trocar Senha
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPw, setShowCurrentPw] = useState(false);
    const [showNewPw, setShowNewPw] = useState(false);
    const [pwSubmitting, setPwSubmitting] = useState(false);
    const [pwSuccessMsg, setPwSuccessMsg] = useState<string | null>(null);
    const [pwErrorMsg, setPwErrorMsg] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setPwSuccessMsg(null);
            setPwErrorMsg(null);
            const loadProfile = async () => {
                try {
                    setLoadingProfile(true);
                    const data = await getUserProfile();
                    setProfileData(data);
                } catch (err) {
                    console.error("Erro ao carregar dados do perfil:", err);
                } finally {
                    setLoadingProfile(false);
                }
            };
            loadProfile();
        }
    }, [isOpen]);

    // Handle ESC key to close modal
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPwSuccessMsg(null);
        setPwErrorMsg(null);

        if (!currentPassword) {
            setPwErrorMsg('Informe sua senha atual.');
            return;
        }

        if (profileData?.SENHA && currentPassword !== profileData.SENHA) {
            setPwErrorMsg('A senha atual informada está incorreta.');
            return;
        }

        if (!newPassword || newPassword.length < 4) {
            setPwErrorMsg('A nova senha deve possuir pelo menos 4 caracteres.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPwErrorMsg('A nova senha e a confirmação não coincidem.');
            return;
        }

        const userId = profileData?.ID_USUARIO || authUser?.id;
        if (!userId) {
            setPwErrorMsg('Não foi possível identificar o usuário atual.');
            return;
        }

        try {
            setPwSubmitting(true);
            await updateUser(Number(userId), { SENHA: newPassword });
            setProfileData((prev: any) => (prev ? { ...prev, SENHA: newPassword } : prev));
            setPwSuccessMsg('Senha alterada com sucesso!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            console.error("Erro ao alterar senha:", err);
            setPwErrorMsg(err?.response?.data?.error || err?.response?.data?.message || 'Falha ao atualizar a senha. Tente novamente.');
        } finally {
            setPwSubmitting(false);
        }
    };

    const userName = profileData?.NOME || authUser?.usuario || 'Usuário';
    const userLogin = profileData?.USUARIO || authUser?.usuario || '--';

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerTitleGroup}>
                        <div className={styles.headerIconWrapper}>
                            <SettingsIcon sx={{ fontSize: 20 }} />
                        </div>
                        <div>
                            <h2 className={styles.headerTitle}>Configurações</h2>
                            <p className={styles.headerSubtitle}>Gerencie seu perfil, segurança e preferências visuais</p>
                        </div>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose} title="Fechar (Esc)">
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </button>
                </div>

                {/* Navigation Tabs */}
                <div className={styles.tabContainer}>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'profile' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('profile')}
                    >
                        <PersonIcon sx={{ fontSize: 18 }} />
                        <span>Perfil e Segurança</span>
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'theme' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('theme')}
                    >
                        {themeMode === 'dark' ? <DarkModeIcon sx={{ fontSize: 18 }} /> : <LightModeIcon sx={{ fontSize: 18 }} />}
                        <span>Aparência</span>
                    </button>
                </div>

                {/* Content Area */}
                <div className={styles.bodyContent}>
                    {/* TAB: PERFIL & SEGURANÇA */}
                    {activeTab === 'profile' && (
                        <div className={styles.profileSection}>
                            <div className={styles.avatarCard}>
                                <div className={styles.userAvatar}>
                                    <PersonIcon sx={{ fontSize: 30 }} />
                                </div>
                                <div className={styles.userMainMeta}>
                                    <h3>{userName}</h3>
                                    <p className={styles.userLoginText}>Usuário de acesso: <strong>{userLogin}</strong></p>
                                </div>
                            </div>

                            <form className={styles.passwordForm} onSubmit={handlePasswordSubmit}>
                                <h4 className={styles.sectionHeading} style={{ marginTop: '4px' }}>Alterar senha</h4>
                                <p className={styles.sectionSubtext}>
                                    Ao alterar a senha aqui, também irá alterar a do DX.
                                </p>
                                {pwSuccessMsg && (
                                    <div className={styles.alertSuccess}>
                                        <CheckCircleIcon sx={{ fontSize: 18 }} />
                                        <span>{pwSuccessMsg}</span>
                                    </div>
                                )}

                                {pwErrorMsg && (
                                    <div className={styles.alertError}>
                                        <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                                        <span>{pwErrorMsg}</span>
                                    </div>
                                )}

                                <div className={styles.fieldGroup}>
                                    <label>Senha Atual</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type={showCurrentPw ? 'text' : 'password'}
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            placeholder="Digite sua senha atual"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className={styles.eyeBtn}
                                            onClick={() => setShowCurrentPw(!showCurrentPw)}
                                        >
                                            {showCurrentPw ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label>Nova Senha</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type={showNewPw ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="Digite a nova senha"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className={styles.eyeBtn}
                                            onClick={() => setShowNewPw(!showNewPw)}
                                        >
                                            {showNewPw ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label>Confirmar Nova Senha</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type={showNewPw ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Repita a nova senha"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className={styles.formActions}>
                                    <button
                                        type="submit"
                                        className={styles.submitBtn}
                                        disabled={pwSubmitting}
                                    >
                                        {pwSubmitting ? 'Salvando...' : 'Alterar Senha'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* TAB: APARÊNCIA & TEMA */}
                    {activeTab === 'theme' && (
                        <div className={styles.themeSection}>
                            <h4 className={styles.sectionHeading}>Escolha o modo visual da aplicação</h4>
                            <p className={styles.sectionSubtext}>Alternar entre os modos claro e escuro ajusta o contraste visual para sua melhor leitura.</p>

                            <div className={styles.themeGrid}>
                                <div
                                    className={`${styles.themeOptionCard} ${themeMode === 'light' ? styles.selectedTheme : ''}`}
                                    onClick={() => onToggleThemeMode('light')}
                                >
                                    <div className={styles.themePreviewLight}>
                                        <div className={styles.previewSidebarLight}></div>
                                        <div className={styles.previewContentLight}>
                                            <div className={styles.previewHeaderLight}></div>
                                            <div className={styles.previewCardsRow}>
                                                <div className={styles.previewCardLight}></div>
                                                <div className={styles.previewCardLight}></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.themeMeta}>
                                        <LightModeIcon className={styles.lightIcon} />
                                        <span>Modo Claro</span>
                                    </div>
                                </div>

                                <div
                                    className={`${styles.themeOptionCard} ${themeMode === 'dark' ? styles.selectedTheme : ''}`}
                                    onClick={() => onToggleThemeMode('dark')}
                                >
                                    <div className={styles.themePreviewDark}>
                                        <div className={styles.previewSidebarDark}></div>
                                        <div className={styles.previewContentDark}>
                                            <div className={styles.previewHeaderDark}></div>
                                            <div className={styles.previewCardsRow}>
                                                <div className={styles.previewCardDark}></div>
                                                <div className={styles.previewCardDark}></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.themeMeta}>
                                        <DarkModeIcon className={styles.darkIcon} />
                                        <span>Modo Escuro</span>
                                    </div>
                                </div>
                            </div>

                            <h4 className={styles.sectionHeading} style={{ marginTop: '32px' }}>Formato da Interface</h4>
                            <p className={styles.sectionSubtext}>Alterne entre cantos arredondados ou quadrados.</p>

                            <div className={styles.themeGrid}>
                                <div
                                    className={`${styles.themeOptionCard} ${themeShape === 'square' ? styles.selectedTheme : ''}`}
                                    onClick={() => onToggleThemeShape && onToggleThemeShape('square')}
                                >
                                    <div className={styles.themeMeta}>
                                        <div style={{ width: 16, height: 16, border: '2px solid currentColor', borderRadius: 2, marginRight: 8 }}></div>
                                        <span>Quadrado</span>
                                    </div>
                                </div>

                                <div
                                    className={`${styles.themeOptionCard} ${themeShape === 'rounded' ? styles.selectedTheme : ''}`}
                                    onClick={() => onToggleThemeShape && onToggleThemeShape('rounded')}
                                >
                                    <div className={styles.themeMeta}>
                                        <div style={{ width: 16, height: 16, border: '2px solid currentColor', borderRadius: 8, marginRight: 8 }}></div>
                                        <span>Arredondado</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsModal;
